import { useEffect, useState } from 'react';
import { X, Cookie } from 'lucide-react';

const CONSENT_KEY = '_en_meta_consent';

/** Slim cookie/analytics consent banner. Shows once until the visitor either
 *  accepts ("granted") or declines ("denied"). The Meta Pixel hook only loads
 *  fbevents.js when consent === 'granted'. */
export default function CookieConsentBar() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(CONSENT_KEY);
      if (!v) setVisible(true);
    } catch {
      // private mode, skip
    }
  }, []);

  const set = (value) => {
    try { localStorage.setItem(CONSENT_KEY, value); } catch {}
    setVisible(false);
    if (value === 'granted') {
      // Reload so the pixel hook re-reads consent on next mount cycle
      try { window.location.reload(); } catch {}
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[55] rounded-2xl bg-[#13153A]/95 backdrop-blur-md border border-purple-500/30 shadow-[0_20px_60px_-15px_rgba(168,85,247,0.5)] p-4 sm:p-5"
      data-testid="cookie-consent-bar"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500/30 to-cyan-500/30 flex items-center justify-center">
          <Cookie size={16} className="text-fuchsia-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold mb-1">A note on cookies</p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            We use cookies for essential functionality + a Meta Pixel to measure ad performance.
            Decline to use the site without analytics tracking.{' '}
            <a href="/privacy" className="text-fuchsia-300 underline hover:text-fuchsia-200">Privacy</a>
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={() => set('granted')}
              className="px-3 py-1.5 rounded-lg bg-yellow-400 text-black text-xs font-bold hover:bg-yellow-300"
              data-testid="consent-accept"
            >
              Accept
            </button>
            <button
              onClick={() => set('denied')}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold hover:bg-white/10"
              data-testid="consent-decline"
            >
              Decline
            </button>
          </div>
        </div>
        <button
          onClick={() => set('denied')}
          aria-label="Dismiss"
          className="text-zinc-400 hover:text-white"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
